import { Context, createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import {
  Application as AppEnum,
  authenticationService,
  FidesTheme,
  GridApi,
  Loading,
  MBS2MRHelper,
  MBSAccessHelper,
  Module,
  Privilege,
  useConfigStore,
} from '@fides/fides-react-components';
import i18n from 'core/i18n';
import { Log } from 'core/helpers/initApp';
import { GlobalConfig } from '../interfaces';
import { userFullData } from '../services';
import { getAppUrl as getAppUrlHelper } from '../helpers';
import { VariantType } from '../enums';
import { ColumnSettings, GridSettings, User } from '../models';
import { LanguageValue } from '../types';

export const isSecurityCodeExpired = (user?: User): boolean => {
  if (!user || user.secondFactorAuthenticated) return false;
  return new Date(user.securityCodeValidUntil) < new Date();
};

export const GlobalConfigContext: Context<GlobalConfig> = createContext<GlobalConfig>({} as GlobalConfig);

export const useGlobalConfigContext = (): GlobalConfig => {
  const context: GlobalConfig = useContext(GlobalConfigContext);

  if (!context) throw new Error('useGlobalConfigContext must be used/wrapped within a GlobalConfigProvider!');

  return context;
};

type GlobalConfigProviderProps = PropsWithChildren & {
  fidesTheme: FidesTheme;
};

export const GlobalConfigProvider: FC<GlobalConfigProviderProps> = ({ fidesTheme, ...rest }) => {
  const [user, setUser] = useState<User>(undefined);
  const [gridApi, setGridApi] = useState<GridApi>(undefined);
  const [checkReleaseNotes, setCheckReleaseNotes] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [variantType, setVariantType] = useState<VariantType>(VariantType.FIDES);
  const accessHelper: MBSAccessHelper = useMemo(() => new MBSAccessHelper(user), [user]);
  const twoMRHelper: MBS2MRHelper = useMemo(() => new MBS2MRHelper(user), [user]);

  const userIsLoggedIn: boolean =
    (authenticationService.isRefreshTokenValid() || authenticationService.isAccessTokenValid()) &&
    !isSecurityCodeExpired(user) &&
    !user?.passwordChangeRequired &&
    !user?.passwordExpired;

  const hasUserAppAccess = (applicationName: AppEnum): boolean =>
    user?.applications?.some((application) => application.name === applicationName && application.enabled) ?? false;

  const isUserAdmin = (): boolean =>
    accessHelper.hasPermissionForModule(Module.USER, Privilege.ADMIN) ||
    accessHelper.isFidesAdministratorUM(AppEnum.EFTWEB);

  const isAnyFidesAdmin = (): boolean => accessHelper.isAnyFidesAdmin();

  useEffect(() => {
    const fetchAndSetUserData = async () => {
      try {
        if ((authenticationService.isRefreshTokenValid() || authenticationService.isAccessTokenValid()) && !user) {
          const userData: User = await userFullData();
          setUser(userData);

          const language: LanguageValue = userData?.settings?.personal?.language;
          if (language) await i18n.changeLanguage(language);
        }
      } catch (error) {
        Log.error('GlobalConfigProvider -> userFullData', error);
        authenticationService.destroyTokens();
      } finally {
        setLoading(false);
      }
    };

    fetchAndSetUserData();
  }, []);

  const logout = (): void => {
    setUser(undefined);
    useConfigStore.setState({ portalUrl: undefined });
  };

  const setGridColumnsSettings = (
    gridName: GridSettings['gridName'],
    pageSize: GridSettings['pagination'],
    colSettings: ColumnSettings[]
  ): void => {
    const newGridsSettings: GridSettings[] | undefined = user.settings.personal.grids?.map(
      (gridSettings: GridSettings) => {
        const newGridSettings: GridSettings = { ...gridSettings };
        if (newGridSettings.gridName === gridName) {
          newGridSettings.columns = colSettings;
          newGridSettings.pagination = pageSize;
        }
        return newGridSettings;
      }
    ) || [{ gridName, columns: colSettings, pagination: pageSize }];

    setUser({
      ...user,
      settings: {
        ...user.settings,
        personal: {
          ...user.settings.personal,
          grids: newGridsSettings,
        },
      },
    });
  };

  const updateGridApi = (api: GridApi): void => setGridApi(api);

  const getAppUrl = (appName: AppEnum): string => getAppUrlHelper(appName, user.applications);

  const globalConfig: GlobalConfig = useMemo(
    () => ({
      accessHelper,
      fidesTheme,
      getAppUrl,
      gridApi,
      hasUserAppAccess,
      isAnyFidesAdmin,
      isUserAdmin,
      logout,
      setGridColumnsSettings,
      setUser,
      setCheckReleaseNotes,
      setVariantType,
      twoMRHelper,
      updateGridApi,
      checkReleaseNotes,
      user,
      userIsLoggedIn,
      variantType,
    }),
    [
      accessHelper,
      fidesTheme,
      getAppUrl,
      gridApi,
      hasUserAppAccess,
      isAnyFidesAdmin,
      isUserAdmin,
      logout,
      setGridColumnsSettings,
      setUser,
      setVariantType,
      twoMRHelper,
      updateGridApi,
      user,
      userIsLoggedIn,
      variantType,
    ]
  );

  return loading ? <Loading /> : <GlobalConfigContext.Provider value={globalConfig} {...rest} />;
};
